/**
 * Автоперевод текста через Google Translate (бесплатно)
 * С кэшированием в localStorage
 */

const CACHE_KEY = 'translationCache'
const CACHE_VERSION = 'v1'

// Получить кэш переводов
const getCache = () => {
    try {
        const cached = localStorage.getItem(CACHE_KEY)
        if (!cached) return {}
        const data = JSON.parse(cached)
        if (data.version !== CACHE_VERSION) return {}
        return data.translations || {}
    } catch {
        return {}
    }
}

// Сохранить в кэш
const saveToCache = (key, value) => {
    try {
        const cache = getCache()
        cache[key] = value
        localStorage.setItem(
            CACHE_KEY,
            JSON.stringify({
                version: CACHE_VERSION,
                translations: cache,
            })
        )
    } catch (e) {
        console.warn('Failed to cache translation:', e)
    }
}

// Генерировать ключ кэша
const getCacheKey = (text, targetLang) => {
    return `${targetLang}:${text.substring(0, 100)}`
}

// Маппинг наших кодов языков на Google Translate коды
const LANG_CODE_MAP = {
    ru: 'ru',
    en: 'en',
    ua: 'uk', // Украинский в Google = uk
    es: 'es',
    pt: 'pt',
    pl: 'pl',
    tr: 'tr',
    ro: 'ro',
    uz: 'uz',
}

// Исключения - не переводить эти слова/фразы (английские названия кейсов)
const NO_TRANSLATE_PHRASES = [
    'The Legend',
    'Furious Case',
    'GGSTANDOFF',
]

// Проверка - нужно ли переводить текст
const shouldTranslate = (text) => {
    if (!text || !text.trim()) return false
    
    // Не переводим если точное совпадение с исключениями
    if (NO_TRANSLATE_PHRASES.includes(text.trim())) return false
    
    // Не переводим если текст полностью на латинице (кроме коротких слов)
    const latinOnly = /^[a-zA-Z0-9\s\-_.]+$/.test(text.trim())
    if (latinOnly && text.trim().length > 3) return false
    
    return true
}

/**
 * Перевод через Google Translate (unofficial API)
 * @param {string} text - Текст для перевода
 * @param {string} targetLang - Целевой язык (en, ru, ua, es, pt, pl, tr, ro, uz)
 * @param {string} sourceLang - Исходный язык (по умолчанию ru)
 * @returns {Promise<string>} - Переведенный текст
 */
export const translateText = async (
    text,
    targetLang = 'en',
    sourceLang = 'ru'
) => {
    // Если текст пустой или язык тот же - возвращаем как есть
    if (!text || !text.trim() || targetLang === sourceLang) {
        return text
    }

    // Проверяем нужно ли переводить (исключения для английских названий)
    if (!shouldTranslate(text)) {
        return text
    }

    // Преобразуем коды языков для Google Translate
    const googleTargetLang = LANG_CODE_MAP[targetLang] || targetLang
    const googleSourceLang = LANG_CODE_MAP[sourceLang] || sourceLang

    // Проверяем кэш
    const cacheKey = getCacheKey(text, targetLang)
    const cache = getCache()
    if (cache[cacheKey]) {
        console.log('📦 Translation from cache:', text.substring(0, 30))
        return cache[cacheKey]
    }

    try {
        // Используем Google Translate через публичный endpoint
        // Это бесплатно но может быть ненадёжно - для продакшена лучше использовать официальный API
        const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${googleSourceLang}&tl=${googleTargetLang}&dt=t&q=${encodeURIComponent(text)}`

        const response = await fetch(url)
        const data = await response.json()

        // Google возвращает массив: [[[переведённый_текст, оригинал, null, null, score]]]
        if (data && data[0] && data[0][0] && data[0][0][0]) {
            const translated = data[0].map((item) => item[0]).join('')

            // Сохраняем в кэш
            saveToCache(cacheKey, translated)

            console.log(
                '🌐 Translated:',
                text.substring(0, 30),
                '→',
                translated.substring(0, 30)
            )
            return translated
        }

        // Если перевод не удался - возвращаем оригинал
        console.warn('Translation failed, returning original')
        return text
    } catch (error) {
        console.error('Translation error:', error)
        // При ошибке возвращаем оригинальный текст
        return text
    }
}

/**
 * Очистить кэш переводов
 */
export const clearTranslationCache = () => {
    try {
        localStorage.removeItem(CACHE_KEY)
        console.log('✅ Translation cache cleared')
    } catch (e) {
        console.error('Failed to clear cache:', e)
    }
}

/**
 * Получить статистику кэша
 */
export const getTranslationCacheStats = () => {
    const cache = getCache()
    const keys = Object.keys(cache)
    return {
        count: keys.length,
        size: new Blob([JSON.stringify(cache)]).size,
    }
}

